import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import {
	PetitionQRCodeData,
	type PetitionQrCodeDataQuery,
	type PetitionQrCodeDataQueryVariables,
} from './getPetitionQRCode.graphql';

export type PetitionQRCodeResult = NonNullable<NonNullable<PetitionQrCodeDataQuery['petition']>['qrCode']> & {
	/** The response's request id, for reports about a payload that loaded but cannot be used. */
	readonly requestId?: string;
};

/**
 * Generates a QR code for a petition.
 *
 * @param slugOrId - The slug or id of the petition
 * @param type - The type of QR code
 * @param size - The size of the QR code in pixels
 * @param utilityContext - The utility context
 * @throws An error if the petition is not found or the QR code is not found
 * @returns The QR code data and the response's request id
 */
export async function getPetitionQRCode({
	slugOrId,
	type,
	size,
	utilityContext,
}: {
	slugOrId: string;
	type: QrCodeType;
	size?: number;
	utilityContext: UtilityContext;
}): Promise<PetitionQRCodeResult> {
	const { data, requestId } = await utilityContext.gql.fetch<PetitionQrCodeDataQuery, PetitionQrCodeDataQueryVariables>(
		{
			query: PetitionQRCodeData,
			variables: {
				slugOrId,
				qrCodeType: type,
				size: size ?? null,
			},
			rejectOnError: true,
		},
	);

	if (!data.petition) throw new Error('Petition not found');
	if (!data.petition.qrCode) throw new Error('QR code not found');

	return { ...data.petition.qrCode, requestId };
}
